interface TextChunk {
    type: "text";
    data: string;
    hasStartDelimiter: boolean;
}

interface LinkChunk {
    type: "link";
    data: string;
}

interface DelimitedChunk {
    type: "math"; // Currently only support math, but there could be others
    data: string;
    rawData: string;
    display: boolean;
}

type FormattedMessageChunk = TextChunk | LinkChunk | DelimitedChunk;

export interface Delimiter {
    type: "math"; // Currently only parse math, but there could be others
    start: string;
    end: string;
    inline?: boolean;
}

// A list of supported TeX delimiters that we will parse for.
// This is exported for tests.
export const TEX_DELIMITERS: Array<Delimiter> = [
    // $...$ and $$...$$ are also valid inline and display TeX delimiters but
    // often result in false-positives. We are omitting them and only going
    // to support the following delimiters for AIguide message rendering.
    {
        type: "math",
        start: "\\(",
        end: "\\)",
        inline: true,
    },
    {
        type: "math",
        start: "\\[",
        end: "\\]",
        inline: false,
    },
];

/**
 * splitAtDelimiters further processes text type message chunks and uses
 * delimiters to parse for formatted content.
 */
// This is exported for tests.
export const splitAtDelimiters = (
    chunks: Array<FormattedMessageChunk>,
    delimiter: Delimiter,
): Array<FormattedMessageChunk> => {
    const parsedChunks: Array<FormattedMessageChunk> = [];
    chunks.forEach((chunk) => {
        // If the chunk isn't a text type, we can assume that it's already
        // been processed and the formatting for it has been identified.
        if (chunk.type !== "text") {
            parsedChunks.push(chunk);
            return;
        }

        // See if there anything we can further parse from this chunk of text
        // using the delimiters passed through.
        const text = chunk.data;
        let currIndex = 0; // The index we've parsed up until.
        let startDelimiterFound = chunk.hasStartDelimiter;
        while (currIndex < text.length) {
            // If we can't find the starting index, then we can assume
            // there isn't anything left to parse.
            const startIndex = text.indexOf(delimiter.start, currIndex);
            if (startIndex === -1) {
                break;
            }
            // If we do have the start index, then we can parse the text
            // up until the delimited content.
            const leadingText = text.slice(currIndex, startIndex);
            currIndex = startIndex;
            startDelimiterFound = true;
            leadingText.length > 0 &&
                parsedChunks.push({
                    type: "text",
                    data: leadingText,
                    hasStartDelimiter: false,
                });

            // If we can't find the ending index, then we can assume
            // there isn't a complete expression to format.
            const endIndex = text.indexOf(
                delimiter.end,
                startIndex + delimiter.start.length,
            );
            if (endIndex === -1) {
                break;
            }
            // If we do have the start and end indices, then we can parse
            // the text for delimited content.
            parsedChunks.push({
                type: delimiter.type,
                data: text.slice(startIndex + delimiter.start.length, endIndex),
                rawData: text.slice(
                    startIndex,
                    endIndex + delimiter.end.length,
                ),
                display: !delimiter.inline,
            });
            currIndex = endIndex + delimiter.end.length;
            startDelimiterFound = false;
        }

        const trailingText = text.slice(currIndex);
        trailingText.length > 0 &&
            parsedChunks.push({
                type: "text",
                data: trailingText,
                hasStartDelimiter: startDelimiterFound,
            });
    });

    return parsedChunks;
};

// From: http://www.regexguru.com/2008/11/detecting-urls-in-a-block-of-text/
const URL_REGEX = new RegExp(
    /((?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$]))/gim,
);
/**
 * splitAtLinks further processes text type message chunks and parses out URL
 * links. NOTE: Links are converted to relative paths.
 */
// This is exported for tests.
export const splitAtLinks = (
    chunks: Array<FormattedMessageChunk>,
): Array<FormattedMessageChunk> => {
    const parsedChunks: Array<FormattedMessageChunk> = [];
    chunks.forEach((chunk) => {
        // If the chunk isn't a text type, we can assume that it's already
        // been processed and the formatting for it has been identified.
        if (chunk.type !== "text") {
            parsedChunks.push(chunk);
            return;
        }
        chunk.data.split(URL_REGEX).forEach((token) => {
            if (URL_REGEX.test(token)) {
                let href = token.replace(/(https?|ftp|file):\/\//, "");
                href = href.substring(href.indexOf("/"));
                parsedChunks.push({type: "link", data: href});
                return;
            }
            parsedChunks.push({
                type: "text",
                data: token,
                hasStartDelimiter: false,
            });
        });
    });
    return parsedChunks;
};

/**
 * parseFormattedText takes in a string and splits it into an array of
 * objects representing a chunk of text and information useful information
 * about it to aid in rendering AIGuide messages.
 *
 * For example:
 * input: "This is a link: https://khanacademy.org/math"
 * output: [
 *  {type: "text", data: "This is a link: "},
 *  {type: "link": data: "/math"}
 * ]
 */
const parseFormattedText = (text: string): Array<FormattedMessageChunk> => {
    // If the text starts with <AIThoughts>, then we need to
    // remove everything until the first </AIThoughts> tag.
    // If we don't have </AIThoughts>, then we can assume that
    // we didn't get it yet, and remove everything.
    const aiThoughtsStartIndex = text.indexOf("<AIThoughts>");
    if (aiThoughtsStartIndex !== -1) {
        const aiThoughtsEndIndex = text.indexOf("</AIThoughts>");
        if (aiThoughtsEndIndex !== -1) {
            text = text.slice(aiThoughtsEndIndex + "</AIThoughts>".length);
            // trim the leading spaces
            text = text.replace(/^\s+/, "");
        } else {
            text = "";
        }
    } else {
        // If the incoming text is starting to write out <AIThoughts>,
        // kill that.
        if ("<AIThoughts>".indexOf(text) !== -1) {
            text = "";
        }
    }

    let textChunks: Array<FormattedMessageChunk> = [
        {type: "text", data: text, hasStartDelimiter: false},
    ];

    // Parses text for URLs
    textChunks = splitAtLinks(textChunks);
    // Parses text for TeX
    TEX_DELIMITERS.forEach((delimiter) => {
        textChunks = splitAtDelimiters(textChunks, delimiter);
    });

    return textChunks;
};

export default parseFormattedText;
